import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import TermsAndConditionsModal from "./TermsAndConditionsModal";

const api_url = process.env.REACT_APP_API_URL;

function Register() {
    const [formData, setFormData] = useState({
        name: "",
        yop: "",
        email: "",
        mobile: "",
        username: "",
        password: "",
        membership: "",
        membershipConfirmation: false, // Added flag for membership confirmation
    });

    const [loading, setLoading] = useState(false);
    const [membershipPlans, setMembershipPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);

    // 🔹 Fetch membership plans dynamically
    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const res = await fetch(`${api_url}/get_membership_plans.php`);
                const data = await res.json();
                if (data.status === "success") {
                    setMembershipPlans(data.plans);
                } else {
                    Swal.fire("Error", "Failed to load membership plans.", "error");
                }
            } catch (err) {
                Swal.fire("Error", "Unable to fetch membership plans.", "error");
            }
        };
        fetchPlans();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (name === "membership") {
            const plan = membershipPlans.find((p) => p.id + '#' + p.category === value);
            setSelectedPlan(plan || null);
        }
    };

    const validateForm = () => {
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
            Swal.fire("Error", "Please enter a valid email address.", "error");
            return false;
        }
        if (!formData.membership) {
            Swal.fire("Error", "Please select a membership type.", "error");
            return false;
        }
        return true;
    };

    // 🔹 Step 1: Register
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.membershipConfirmation) {
        Swal.fire("Agreement Required", "You must agree to the Terms and Conditions before registering.", "warning");
        return;
    }
        if (!validateForm()) return;
    
        setLoading(true);

        try {
            const response = await fetch(`${api_url}/register.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const result = await response.json();

            if (result.status === "success") {
                sendOTP(formData.email);
            } else {
                Swal.fire("Error", result.message, "error");
                setLoading(false);
            }
        } catch (err) {
            Swal.fire("Error", "Registration failed.", "error");
            setLoading(false);
        }
    };

    // 🔹 Step 2: Send OTP
    const sendOTP = async (email) => {
        try {
            const response = await fetch(`${api_url}/send_otp.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            const result = await response.json();

            if (result.status === "success") {
                Swal.fire({
                    icon: "success",
                    title: "OTP Sent",
                    text: "Please check your email for OTP.",
                }).then(() => promptForOTP(email));
            } else {
                Swal.fire("Error", result.message, "error");
                setLoading(false);
            }
        } catch (err) {
            Swal.fire("Error", "Failed to send OTP.", "error");
            setLoading(false);
        }
    };

    // 🔹 Step 3: Prompt OTP input
    const promptForOTP = (email) => {
        Swal.fire({
            title: "Email Verification",
            input: "text",
            inputLabel: `Enter OTP sent to ${email}`,
            inputPlaceholder: "Enter OTP",
            showCancelButton: true,
            confirmButtonText: "Verify OTP",
            allowOutsideClick: false,
            preConfirm: (otp) => {
                if (!otp) {
                    Swal.showValidationMessage("Please enter the OTP");
                }
                return otp;
            },
        }).then((result) => {
            if (result.isConfirmed) verifyOTP(email, result.value);
            else setLoading(false);
        });
    };

    // 🔹 Step 4: Verify OTP
    const verifyOTP = async (email, otp) => {
        try {
            const res = await fetch(`${api_url}/verify_otp.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, otp }),
            });
            const result = await res.json();

            if (result.status === "success") {
                const totalPayable =
                    (parseFloat(selectedPlan?.security_deposit) || 0) +
                    (parseFloat(selectedPlan?.lifetime_fee) || 0) +
                    (parseFloat(selectedPlan?.annual_fee) || 0);

                if (totalPayable > 0) {
                    Swal.fire("Email Verified", "Proceeding to payment...", "success").then(() => {
                        startRazorpayPayment(email, totalPayable, formData.membership);
                    });
                } else {
                    Swal.fire("Success", "Registration complete! You can now login.", "success").then(
                        () => (window.location.href = "/login")
                    );
                }
            } else {
                Swal.fire("Error", result.message || "Invalid OTP.", "error").then(() =>
                    promptForOTP(email)
                );
            }
        } catch (err) {
            Swal.fire("Error", "OTP verification failed.", "error");
            setLoading(false);
        }
    };

    // 🔹 Step 5: Razorpay payment
    const startRazorpayPayment = async (email, amount, membership) => {
        try {
            const orderRes = await fetch(`${api_url}/payment/create_reg_order.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ amount, email, membership }),
            });
            const orderData = await orderRes.json();

            if (orderData.status !== "success") {
                Swal.fire("Error", "Unable to create payment order.", "error");
                return;
            }

            const options = {
                key: orderData.key,
                amount: orderData.amount,
                currency: "INR",
                name: "Alumni Association",
                description: "Membership Fee Payment",
                order_id: orderData.order_id,
                handler: async function (response) {
                    const verifyRes = await fetch(`${api_url}/payment/verify_reg_payment.php`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature,
                            email,
                        }),
                    });
                    const verifyData = await verifyRes.json();

                    if (verifyData.status === "success") {
                        Swal.fire("Success", "Payment successful! Registration complete.", "success").then(
                            () => (window.location.href = "/login")
                        );
                    } else {
                        Swal.fire("Error", "Payment verification failed.", "error");
                    }
                },
                prefill: {
                    name: formData.name,
                    email: formData.email,
                    contact: formData.mobile,
                },
                theme: { color: "#0d6efd" },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (err) {
            Swal.fire("Error", `Payment failed: ${err.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Header */}
            <div className="container-fluid bg-primary py-5 mb-5 page-header">
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-10 text-center">
                            <h1 className="display-3 text-white animated slideInDown">Register</h1>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb justify-content-center">
                                    <li className="breadcrumb-item">
                                        <Link className="text-white" to="#">
                                            Home
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link className="text-white" to="#">
                                            Pages
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item text-white active" aria-current="page">
                                        Register
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className="container-xxl py-5 wow fadeInUp" data-wow-delay="0.1s">
                <form onSubmit={handleSubmit}>
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="card col-md-12 login-card">
                                <div className="card-body">
                                    <h3 className="text-center">Register</h3><br />
                                    <div className="row justify-content-between g-3">
                                        {[
                                            { label: "Name", name: "name", type: "text" },
                                            { label: "Year of Passed out", name: "yop", type: "text" },
                                            { label: "Email Address", name: "email", type: "email" },
                                            { label: "Mobile", name: "mobile", type: "text" },
                                            { label: "Username", name: "username", type: "text" },
                                            { label: "Password", name: "password", type: "password" },
                                        ].map(({ label, name, type }) => (
                                            <div className="form-group col-md-6" key={name}>
                                                <label htmlFor={name}>{label}</label>
                                                <input
                                                    type={type}
                                                    className="form-control"
                                                    name={name}
                                                    id={name}
                                                    placeholder={label}
                                                    value={formData[name]}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        ))}

                                        <div className="form-group col-md-6">
                                            <label htmlFor="membership">Membership Type</label>
                                            <select
                                                name="membership"
                                                id="membership"
                                                className="form-control"
                                                value={formData.membership}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Membership</option>
                                                {membershipPlans.map((plan) => (
                                                    <option key={plan.id} value={plan.id + '#' + plan.category}>
                                                        {plan.display_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {selectedPlan && (
                                            <div className="form-group col-md-6">
                                                <label>Fee Details</label>
                                                <ul className="list-group">
                                                    <li className="list-group-item">
                                                        Security Deposit: ₹{selectedPlan.security_deposit}
                                                    </li>
                                                    <li className="list-group-item">
                                                        Lifetime Fee: ₹{selectedPlan.lifetime_fee}
                                                    </li>
                                                    <li className="list-group-item">
                                                        Annual Fee: ₹{selectedPlan.annual_fee}
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>


                                <div className="form-group col-md-6 d-flex align-items-start">
                                    <input
                                        type="checkbox"
                                        id="membershipConfirmation"
                                        className="form-check-input me-2 mt-1"
                                        checked={formData.membershipConfirmation}
                                        onChange={(e) =>
                                            setFormData({ ...formData, membershipConfirmation: e.target.checked })
                                        }
                                    />
                                    <label htmlFor="membershipConfirmation" className="form-check-label fw-bold">
                                        I have read and agree to the{" "}
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 align-baseline"
                                            data-bs-toggle="modal"
                                            data-bs-target="#termsModal"
                                        >
                                            Terms and Conditions
                                        </button>
                                    </label>
                                </div>



                                <div className="row justify-content-center g-3 mb-3">
                                    <div className="col-md-6 text-end">
                                        <button
                                            type="submit"
                                            className="btn-login"
                                            disabled={loading}
                                        >
                                            <b>{loading ? "Processing..." : "Register"}</b>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

               <TermsAndConditionsModal />

            </div>
        </>
    );
}

export default Register;
